import React from 'react';
import { ToolConfig, ToolId } from '../workspace-config';
import { cn } from '../../../utils/cn';
import { Button, Icon } from '../../../components/common';

interface PanelToolPickerProps {
    /** Available tools to pick from */
    tools: ToolConfig[];
    /** Tool to exclude from the list (the active/left-panel tool) */
    excludeToolId: ToolId;
    /** Called when user selects a tool */
    onSelect: (toolId: ToolId) => void;
}

/**
 * PanelToolPicker — displayed in the right split panel when no companion tool
 * has been selected yet. Shows a grid of available workspace tools for the user
 * to choose which tool to pair alongside their active tool.
 */
export const PanelToolPicker: React.FC<PanelToolPickerProps> = ({
    tools,
    excludeToolId,
    onSelect,
}) => {
    const availableTools = tools.filter(t => t.id !== excludeToolId);

    return (
        <div className="flex h-full min-h-0 flex-col items-center overflow-y-auto p-6">
            {/* Header */}
            <div className="mb-8 shrink-0 text-center">
                <Icon name="vertical_split" size="xl" className="text-primary/60 dark:text-primary/40 mb-3 block" />
                <h3 className="text-lg font-semibold text-primary dark:text-white mb-1">
                    Open a Tool
                </h3>
                <p className="text-sm text-secondary dark:text-secondary-dark max-w-xs">
                    Choose which tool to view alongside your current panel.
                </p>
            </div>

            {/* Tool Grid */}
            <div className="@container w-full max-w-md shrink-0">
                <div className="grid grid-cols-2 @sm:grid-cols-3 gap-3 w-full">
                    {availableTools.map(tool => (
                        <Button
                            key={tool.id}
                            onClick={() => onSelect(tool.id)}
                            size="sm"
                            variant="ghost"
                            className={cn(
                                'flex min-h-0 flex-col items-center gap-2 rounded-xl p-4 shadow-none',
                                'bg-white/60 dark:bg-white/5',
                                'border border-gray-200/60 dark:border-white/10',
                                'hover:bg-primary/8 hover:border-primary/30 dark:hover:bg-primary/10 dark:hover:border-primary/30',
                                'transition-all duration-200 ease-out',
                                'group cursor-pointer',
                            )}
                        >
                            <Icon
                                name={tool.icon}
                                size="lg"
                                className={cn(
                                    'text-gray-500 dark:text-gray-400',
                                    'group-hover:text-primary dark:group-hover:text-primary',
                                    'transition-colors duration-200',
                                )}
                            />
                            <span className={cn(
                                'text-xs font-medium text-center leading-tight',
                                'text-gray-600 dark:text-gray-300',
                                'group-hover:text-primary dark:group-hover:text-primary',
                                'transition-colors duration-200',
                            )}>
                                {tool.label}
                            </span>
                        </Button>
                    ))}
                </div>
            </div>

            {availableTools.length === 0 && (
                <p className="shrink-0 text-sm text-secondary dark:text-secondary-dark italic">
                    No other tools available in this workspace.
                </p>
            )}
        </div>
    );
};
